import {
  AlertCircle,
  BookOpen,
  Check,
  Download,
  Eye,
  FileText,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { SearchableDropdown } from '../components/forms';
import Pagination from '../components/Pagination';
import { EmptyState, ModuleLayout } from '../components/ui';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import specificationService from '../services/specificationService';
import uploadService from '../services/uploadService';
import {
  CreateSpecificationData,
  CreateSubstitutionData,
  CSIDivision,
  ProductSubstitution,
  ProjectSpecification,
  ReviewSubstitutionData,
  SpecificationQuery,
  SpecificationStatus,
  SubstitutionQuery,
  SubstitutionStatus,
} from '../types';

type TabType = 'specifications' | 'substitutions';

const Specifications: React.FC = () => {
  const projectId = useCurrentProjectId();
  const [activeTab, setActiveTab] = useState<TabType>('specifications');
  const [specifications, setSpecifications] = useState<ProjectSpecification[]>([]);
  const [substitutions, setSubstitutions] = useState<ProductSubstitution[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpecs, setSelectedSpecs] = useState<string[]>([]);
  const [csiDivisions, setCsiDivisions] = useState<CSIDivision[]>([]);

  // Drawers
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showSubstitutionDrawer, setShowSubstitutionDrawer] = useState(false);
  const [showReviewDrawer, setShowReviewDrawer] = useState(false);
  const [selectedSpec, setSelectedSpec] = useState<ProjectSpecification | null>(null);
  const [selectedSubstitution, setSelectedSubstitution] = useState<ProductSubstitution | null>(
    null
  );

  // Pagination - Specifications
  const [specPage, setSpecPage] = useState(1);
  const [specTotalPages, setSpecTotalPages] = useState(1);
  const [specTotal, setSpecTotal] = useState(0);

  // Pagination - Substitutions
  const [subPage, setSubPage] = useState(1);
  const [subTotalPages, setSubTotalPages] = useState(1);
  const [subTotal, setSubTotal] = useState(0);

  // Filters
  const [specFilters, setSpecFilters] = useState<SpecificationQuery>({
    search: '',
    division: undefined,
    status: undefined,
    sort_by: 'section_number',
    sort_order: 'asc',
    page: 1,
    limit: 20,
  });

  const [subFilters, setSubFilters] = useState<SubstitutionQuery>({
    status: undefined,
    sort_by: 'submitted_at',
    sort_order: 'desc',
    page: 1,
    limit: 20,
  });

  // Load CSI divisions
  useEffect(() => {
    const loadDivisions = async () => {
      const result = await specificationService.getCSIDivisions();
      if (result.success) {
        setCsiDivisions(result.divisions);
      }
    };
    loadDivisions();
  }, []);

  // Load specifications
  const loadSpecifications = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const query: SpecificationQuery = {
        ...specFilters,
        page: specPage,
      };
      const result = await specificationService.getSpecifications(projectId, query);
      if (result.success) {
        setSpecifications(result.specifications);
        setSpecTotalPages(result.totalPages);
        setSpecTotal(result.total);
      }
    } catch (error) {
      console.error('Failed to load specifications:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, specFilters, specPage]);

  // Load substitutions
  const loadSubstitutions = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const query: SubstitutionQuery = {
        ...subFilters,
        page: subPage,
      };
      const result = await specificationService.getSubstitutions(projectId, query);
      if (result.success) {
        setSubstitutions(result.substitutions);
        setSubTotalPages(result.totalPages);
        setSubTotal(result.total);
      }
    } catch (error) {
      console.error('Failed to load substitutions:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, subFilters, subPage]);

  useEffect(() => {
    if (activeTab === 'specifications') {
      loadSpecifications();
    } else {
      loadSubstitutions();
    }
  }, [activeTab, loadSpecifications, loadSubstitutions]);

  // Handle filter changes
  const handleSpecFilterChange = (key: keyof SpecificationQuery, value: string | undefined) => {
    setSpecFilters(prev => ({ ...prev, [key]: value }));
    setSpecPage(1);
  };

  const handleSubFilterChange = (key: keyof SubstitutionQuery, value: string | undefined) => {
    setSubFilters(prev => ({ ...prev, [key]: value }));
    setSubPage(1);
  };

  // Toggle spec selection
  const toggleSpecSelection = (specId: string) => {
    setSelectedSpecs(prev =>
      prev.includes(specId) ? prev.filter(id => id !== specId) : [...prev, specId]
    );
  };

  // Delete specifications
  const handleDeleteSpecs = async () => {
    if (!projectId || selectedSpecs.length === 0) return;

    if (window.confirm(`Delete ${selectedSpecs.length} specification(s)?`)) {
      for (const specId of selectedSpecs) {
        await specificationService.deleteSpecification(projectId, specId);
      }
      setSelectedSpecs([]);
      loadSpecifications();
    }
  };

  // View spec details
  const handleViewSpec = (spec: ProjectSpecification) => {
    setSelectedSpec(spec);
    setShowDetailsDrawer(true);
  };

  // Request substitution
  const handleRequestSubstitution = (spec: ProjectSpecification) => {
    setSelectedSpec(spec);
    setShowSubstitutionDrawer(true);
  };

  // Review substitution
  const handleReviewSubstitution = (sub: ProductSubstitution) => {
    setSelectedSubstitution(sub);
    setShowReviewDrawer(true);
  };

  // Get status badge
  const getStatusBadge = (
    status: SpecificationStatus | SubstitutionStatus,
    type: 'spec' | 'sub'
  ) => {
    const colorClass =
      type === 'spec'
        ? specificationService.getStatusColor(status)
        : specificationService.getSubstitutionStatusColor(status);
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${colorClass}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  // Stats
  const stats = [
    {
      title: 'Specifications',
      value: specTotal,
      icon: <FileText className="h-6 w-6" />,
      variant: 'brand' as const,
    },
    {
      title: 'Substitution Requests',
      value: subTotal,
      icon: <AlertCircle className="h-6 w-6" />,
      variant: 'info' as const,
    },
  ];

  // Division options for dropdown
  const divisionOptions = [
    { value: '', label: 'All Divisions' },
    ...csiDivisions.map(d => ({ value: d.code, label: `${d.code} - ${d.name}` })),
  ];

  // Status options
  const specStatusOptions = [
    { value: '', label: 'All Statuses' },
    ...specificationService
      .getSpecificationStatuses()
      .map(s => ({ value: s.value, label: s.label })),
  ];

  const subStatusOptions = [
    { value: '', label: 'All Statuses' },
    ...specificationService
      .getSubstitutionStatuses()
      .map(s => ({ value: s.value, label: s.label })),
  ];

  // Sort options
  const sortOptions = specificationService.getSortOptions().map(s => ({
    value: s.value,
    label: s.label,
  }));

  return (
    <>
      <ModuleLayout
        title={projectId ? 'Project Specifications' : 'All Specifications'}
        description={
          projectId
            ? 'Manage technical specifications and construction standards'
            : 'Browse specifications across all projects'
        }
        icon={<BookOpen className="h-4 w-4 text-brand-600" />}
        primaryAction={{
          label: 'Upload Specification',
          onClick: () => setShowUploadDrawer(true),
          icon: <Plus className="h-4 w-4 mr-2" />,
        }}
        onRefresh={() =>
          activeTab === 'specifications' ? loadSpecifications() : loadSubstitutions()
        }
        isLoading={loading}
        stats={stats}
        searchValue={activeTab === 'specifications' ? specFilters.search || '' : ''}
        onSearchChange={
          activeTab === 'specifications'
            ? value => handleSpecFilterChange('search', value)
            : undefined
        }
        filters={
          activeTab === 'specifications' ? (
            <div className="flex items-center gap-2">
              <SearchableDropdown
                placeholder="Division"
                value={specFilters.division || ''}
                onChange={value => handleSpecFilterChange('division', value || undefined)}
                options={divisionOptions}
              />
              <SearchableDropdown
                placeholder="Status"
                value={specFilters.status || ''}
                onChange={value => handleSpecFilterChange('status', value as SpecificationStatus)}
                options={specStatusOptions}
              />
              <SearchableDropdown
                placeholder="Sort By"
                value={specFilters.sort_by || 'section_number'}
                onChange={value => handleSpecFilterChange('sort_by', value)}
                options={sortOptions}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <SearchableDropdown
                placeholder="Status"
                value={subFilters.status || ''}
                onChange={value => handleSubFilterChange('status', value as SubstitutionStatus)}
                options={subStatusOptions}
              />
            </div>
          )
        }
      >
        {/* Tabs */}
        <div className="flex items-center space-x-4 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('specifications')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'specifications'
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Specifications ({specTotal})
          </button>
          <button
            onClick={() => setActiveTab('substitutions')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'substitutions'
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Substitution Requests ({subTotal})
          </button>
        </div>

        {/* Selected Actions */}
        {activeTab === 'specifications' && selectedSpecs.length > 0 && (
          <div className="flex items-center justify-between bg-brand-50 border border-brand-200 rounded-lg px-4 py-2 mb-4">
            <span className="text-sm text-brand-700">
              {selectedSpecs.length} specification(s) selected
            </span>
            <button
              onClick={handleDeleteSpecs}
              className="flex items-center px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 text-brand-600 animate-spin" />
          </div>
        ) : activeTab === 'specifications' ? (
          specifications.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No specifications found"
              description="Upload specification documents to manage technical requirements and construction standards."
              action={{
                label: 'Upload Specification',
                onClick: () => setShowUploadDrawer(true),
              }}
            />
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedSpecs(specifications.map(s => s._id));
                          } else {
                            setSelectedSpecs([]);
                          }
                        }}
                        checked={
                          selectedSpecs.length === specifications.length &&
                          specifications.length > 0
                        }
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Section
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Division
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Version
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modified
                    </th>
                    <th className="w-32 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {specifications.map(spec => (
                    <tr key={spec._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={selectedSpecs.includes(spec._id)}
                          onChange={() => toggleSpecSelection(spec._id)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-medium text-brand-600">
                          {specificationService.formatSectionNumber(spec.section_number)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{spec.title}</span>
                        {spec.revision && (
                          <span className="ml-2 text-xs text-gray-500">Rev {spec.revision}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm">
                        {spec.division} - {specificationService.getDivisionName(spec.division)}
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(spec.status, 'spec')}</td>
                      <td className="px-4 py-3 text-gray-500 text-sm">v{spec.current_version}</td>
                      <td className="px-4 py-3 text-gray-500 text-sm">
                        {new Date(spec.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleViewSpec(spec)}
                            className="p-1.5 hover:bg-gray-100 rounded"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => window.open(spec.url, '_blank')}
                            className="p-1.5 hover:bg-gray-100 rounded"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleRequestSubstitution(spec)}
                            className="p-1.5 hover:bg-gray-100 rounded"
                            title="Request Substitution"
                          >
                            <AlertCircle className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : substitutions.length === 0 ? (
          <EmptyState
            icon={AlertCircle}
            title="No substitution requests"
            description="Product substitution requests will appear here when submitted."
          />
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Specification
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Original Product
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Proposed Product
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manufacturer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="w-24 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {substitutions.map(sub => (
                  <tr key={sub._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm text-brand-600">
                      {sub.substitution_id}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm">{sub.spec_section}</span>
                      <span className="ml-2 text-gray-500">{sub.spec_title}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{sub.original_product}</td>
                    <td className="px-4 py-3 font-medium">{sub.proposed_product}</td>
                    <td className="px-4 py-3 text-gray-500">{sub.manufacturer}</td>
                    <td className="px-4 py-3">{getStatusBadge(sub.status, 'sub')}</td>
                    <td className="px-4 py-3 text-gray-500 text-sm">
                      {new Date(sub.submitted_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleReviewSubstitution(sub)}
                        className="flex items-center px-3 py-1 text-sm text-brand-600 hover:bg-brand-50 rounded"
                      >
                        {sub.status === 'submitted' || sub.status === 'under_review' ? (
                          <>
                            <Eye className="h-4 w-4 mr-1" />
                            Review
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {activeTab === 'specifications' && specTotalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={specPage}
              totalPages={specTotalPages}
              totalItems={specTotal}
              itemsPerPage={specFilters.limit || 20}
              onPageChange={setSpecPage}
            />
          </div>
        )}
        {activeTab === 'substitutions' && subTotalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={subPage}
              totalPages={subTotalPages}
              totalItems={subTotal}
              itemsPerPage={subFilters.limit || 20}
              onPageChange={setSubPage}
            />
          </div>
        )}
      </ModuleLayout>

      {/* Upload Specification Drawer */}
      {showUploadDrawer &&
        createPortal(
          <UploadSpecificationDrawer
            projectId={projectId || ''}
            divisions={csiDivisions}
            onClose={() => setShowUploadDrawer(false)}
            onUploaded={() => {
              setShowUploadDrawer(false);
              loadSpecifications();
            }}
          />,
          document.body
        )}

      {/* Specification Details Drawer */}
      {showDetailsDrawer &&
        selectedSpec &&
        createPortal(
          <SpecificationDetailsDrawer
            projectId={projectId || ''}
            specification={selectedSpec}
            onClose={() => {
              setShowDetailsDrawer(false);
              setSelectedSpec(null);
            }}
            onRequestSubstitution={() => {
              setShowDetailsDrawer(false);
              setShowSubstitutionDrawer(true);
            }}
          />,
          document.body
        )}

      {/* Request Substitution Drawer */}
      {showSubstitutionDrawer &&
        selectedSpec &&
        createPortal(
          <RequestSubstitutionDrawer
            projectId={projectId || ''}
            specification={selectedSpec}
            onClose={() => {
              setShowSubstitutionDrawer(false);
              setSelectedSpec(null);
            }}
            onSubmitted={() => {
              setShowSubstitutionDrawer(false);
              setSelectedSpec(null);
              loadSubstitutions();
              setActiveTab('substitutions');
            }}
          />,
          document.body
        )}

      {/* Review Substitution Drawer */}
      {showReviewDrawer &&
        selectedSubstitution &&
        createPortal(
          <ReviewSubstitutionDrawer
            projectId={projectId || ''}
            substitution={selectedSubstitution}
            onClose={() => {
              setShowReviewDrawer(false);
              setSelectedSubstitution(null);
            }}
            onReviewed={() => {
              setShowReviewDrawer(false);
              setSelectedSubstitution(null);
              loadSubstitutions();
            }}
          />,
          document.body
        )}
    </>
  );
};

// ============================
// Upload Specification Drawer
// ============================
interface UploadSpecificationDrawerProps {
  projectId: string;
  divisions: CSIDivision[];
  onClose: () => void;
  onUploaded: () => void;
}

const UploadSpecificationDrawer: React.FC<UploadSpecificationDrawerProps> = ({
  projectId,
  divisions,
  onClose,
  onUploaded,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<'idle' | 'uploading' | 'saving' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [uploadedFileKey, setUploadedFileKey] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateSpecificationData>({
    title: '',
    section_number: '',
    division: '',
    description: '',
    revision: '',
    prepared_by: '',
    tags: [],
    submittal_required: false,
  });

  // Validate form before submission
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!file) {
      errors.file = 'Please select a file to upload';
    }

    if (!formData.section_number.trim()) {
      errors.section_number = 'Section number is required';
    } else if (!/^[\d\s]+$/.test(formData.section_number.replace(/\s/g, ''))) {
      errors.section_number = 'Section number should contain only numbers';
    }

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.trim().length < 3) {
      errors.title = 'Title must be at least 3 characters';
    }

    if (!formData.division) {
      errors.division = 'Division is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setError(null);
    setSuccessMessage(null);

    // Validate form first (before any upload)
    if (!validateForm()) {
      return;
    }

    if (!file || !projectId) {
      setError('Missing required file or project');
      return;
    }

    setUploading(true);
    setUploadStage('uploading');

    try {
      // Step 1: Upload to S3
      const uploadResult = await uploadService.uploadFile(file, {
        module: 'specifications',
        projectId,
        onProgress: setUploadProgress,
      });

      if (!uploadResult.success) {
        setError(uploadResult.error || 'File upload failed. Please try again.');
        setUploadStage('idle');
        setUploading(false);
        return;
      }

      if (!uploadResult.data) {
        setError('Upload completed but no file reference received. Please try again.');
        setUploadStage('idle');
        setUploading(false);
        return;
      }

      // Store the uploaded file key in case we need to retry specification creation
      const uploadData = uploadResult.data as { key: string; [key: string]: unknown };
      setUploadedFileKey(uploadData.key);

      // Step 2: Create specification record
      setUploadStage('saving');
      const result = await specificationService.createSpecification(
        projectId,
        {
          key: uploadData.key,
          name: file.name,
          size: file.size,
          type: file.type,
        },
        formData
      );

      if (result.success) {
        setUploadStage('done');
        setSuccessMessage('Specification uploaded successfully!');
        // Wait a moment to show success, then close
        setTimeout(() => {
          onUploaded();
        }, 1000);
      } else {
        // Keep drawer open with error so user can fix and retry
        const errorMessage = result.message || 'Failed to save specification details';
        setError(errorMessage);
        setUploadStage('idle');

        // Parse validation errors if available
        if (errorMessage.toLowerCase().includes('validation')) {
          setError('Please check the form fields and try again.');
        }
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setError(
        err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.'
      );
      setUploadStage('idle');
    } finally {
      if (uploadStage !== 'done') {
        setUploading(false);
        setUploadProgress(0);
      }
    }
  };

  // Retry saving specification with already uploaded file
  const handleRetrySave = async () => {
    if (!uploadedFileKey || !file || !projectId) return;

    // Validate form first
    if (!validateForm()) {
      return;
    }

    setError(null);
    setUploading(true);
    setUploadStage('saving');

    try {
      const result = await specificationService.createSpecification(
        projectId,
        {
          key: uploadedFileKey,
          name: file.name,
          size: file.size,
          type: file.type,
        },
        formData
      );

      if (result.success) {
        setUploadStage('done');
        setSuccessMessage('Specification saved successfully!');
        setTimeout(() => {
          onUploaded();
        }, 1000);
      } else {
        setError(result.message || 'Failed to save specification. Please try again.');
        setUploadStage('idle');
      }
    } catch (err) {
      console.error('Save failed:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
      setUploadStage('idle');
    } finally {
      if (uploadStage !== 'done') {
        setUploading(false);
      }
    }
  };

  const handleFieldChange = (
    field: keyof CreateSpecificationData,
    value: string | boolean | string[]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
    // Clear general error when user makes changes
    if (error) {
      setError(null);
    }
  };

  const handleFileChange = (newFile: File | null) => {
    setFile(newFile);
    // Reset uploaded file key if user changes file
    setUploadedFileKey(null);
    // Clear file error
    if (fieldErrors.file) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated.file;
        return updated;
      });
    }
    if (error) {
      setError(null);
    }
  };

  // Auto-set division from section number
  useEffect(() => {
    if (formData.section_number.length >= 2) {
      const divCode = formData.section_number.replace(/\s/g, '').substring(0, 2);
      if (divisions.some(d => d.code === divCode)) {
        setFormData(prev => ({ ...prev, division: divCode }));
      }
    }
  }, [formData.section_number, divisions]);

  const isFormValid =
    file && formData.section_number.trim() && formData.title.trim() && formData.division;
  const canRetry = uploadedFileKey && !uploading;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={() => !uploading && onClose()}
      />
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[600px] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Upload className="h-5 w-5 text-brand-600" />
              <h2 className="text-lg font-semibold">Upload Specification</h2>
            </div>
            <button
              onClick={() => !uploading && onClose()}
              className="p-2 hover:bg-gray-100 rounded-lg"
              disabled={uploading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Success Message */}
            {successMessage && (
              <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                <Check className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{successMessage}</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium">Error</p>
                  <p className="text-sm mt-1">{error}</p>
                  {canRetry && (
                    <button
                      type="button"
                      onClick={handleRetrySave}
                      className="mt-2 text-sm font-medium text-red-800 hover:text-red-900 underline"
                    >
                      File already uploaded - Click to retry saving specification
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* File Upload */}
            <div>
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                  fieldErrors.file
                    ? 'border-red-400 bg-red-50'
                    : file
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-gray-300 hover:border-brand-500'
                }`}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  if (!uploading) {
                    const droppedFile = e.dataTransfer.files[0];
                    if (droppedFile) handleFileChange(droppedFile);
                  }
                }}
              >
                {file ? (
                  <div className="flex items-center justify-center space-x-3">
                    <FileText className="h-8 w-8 text-brand-600" />
                    <div className="text-left">
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      {uploadedFileKey && (
                        <p className="text-xs text-green-600 mt-1">
                          <Check className="h-3 w-3 inline mr-1" />
                          File uploaded to server
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleFileChange(null)}
                      className="p-1 hover:bg-gray-100 rounded"
                      disabled={uploading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-2">
                      Drag and drop file here, or click to browse
                    </p>
                    <input
                      type="file"
                      id="spec-file"
                      className="hidden"
                      accept=".pdf,.doc,.docx"
                      onChange={e => e.target.files && handleFileChange(e.target.files[0])}
                      disabled={uploading}
                    />
                    <label
                      htmlFor="spec-file"
                      className="inline-flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg cursor-pointer hover:bg-brand-700"
                    >
                      Browse Files
                    </label>
                    <p className="text-xs text-gray-500 mt-2">Accepted formats: PDF, DOC, DOCX</p>
                  </>
                )}
              </div>
              {fieldErrors.file && <p className="text-sm text-red-600 mt-1">{fieldErrors.file}</p>}
            </div>

            {/* Section Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Section Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.section_number}
                onChange={e => handleFieldChange('section_number', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
                  fieldErrors.section_number ? 'border-red-400 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="e.g., 03 30 00"
                disabled={uploading}
              />
              {fieldErrors.section_number ? (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.section_number}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">CSI MasterFormat section number</p>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={e => handleFieldChange('title', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
                  fieldErrors.title ? 'border-red-400 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="e.g., Cast-in-Place Concrete"
                disabled={uploading}
              />
              {fieldErrors.title && (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.title}</p>
              )}
            </div>

            {/* Division */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Division <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.division}
                onChange={e => handleFieldChange('division', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
                  fieldErrors.division ? 'border-red-400 bg-red-50' : 'border-gray-300'
                }`}
                disabled={uploading}
              >
                <option value="">Select Division</option>
                {divisions.map(d => (
                  <option key={d.code} value={d.code}>
                    {d.code} - {d.name}
                  </option>
                ))}
              </select>
              {fieldErrors.division && (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.division}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={e => handleFieldChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                rows={3}
                placeholder="Brief description of the specification"
                disabled={uploading}
              />
            </div>

            {/* Revision */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Revision</label>
                <input
                  type="text"
                  value={formData.revision}
                  onChange={e => handleFieldChange('revision', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="e.g., A, B, 1"
                  disabled={uploading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prepared By</label>
                <input
                  type="text"
                  value={formData.prepared_by}
                  onChange={e => handleFieldChange('prepared_by', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Author name"
                  disabled={uploading}
                />
              </div>
            </div>

            {/* Submittal Required */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="submittal-required"
                checked={formData.submittal_required}
                onChange={e => handleFieldChange('submittal_required', e.target.checked)}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                disabled={uploading}
              />
              <label htmlFor="submittal-required" className="text-sm text-gray-700">
                Submittal Required
              </label>
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium">
                    {uploadStage === 'uploading' && 'Uploading file...'}
                    {uploadStage === 'saving' && 'Saving specification...'}
                    {uploadStage === 'done' && 'Complete!'}
                  </span>
                  {uploadStage === 'uploading' && <span>{uploadProgress}%</span>}
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      uploadStage === 'done' ? 'bg-green-500' : 'bg-brand-600'
                    }`}
                    style={{
                      width: uploadStage === 'uploading' ? `${uploadProgress}%` : '100%',
                    }}
                  />
                </div>
                {uploadStage === 'saving' && (
                  <p className="text-xs text-gray-500 mt-2">
                    File uploaded successfully. Saving specification details...
                  </p>
                )}
              </div>
            )}
          </form>

          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={uploading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              {canRetry ? (
                <button
                  type="button"
                  onClick={handleRetrySave}
                  disabled={uploading || !isFormValid}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  Retry Save
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={uploading || !isFormValid}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {uploading ? (
                    <span className="flex items-center">
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {uploadStage === 'uploading' ? 'Uploading...' : 'Saving...'}
                    </span>
                  ) : (
                    'Upload Specification'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================
// Specification Details Drawer
// ============================
interface SpecificationDetailsDrawerProps {
  projectId: string;
  specification: ProjectSpecification;
  onClose: () => void;
  onRequestSubstitution: () => void;
}

const SpecificationDetailsDrawer: React.FC<SpecificationDetailsDrawerProps> = ({
  projectId: _projectId,
  specification,
  onClose,
  onRequestSubstitution,
}) => {
  return (
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[700px] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <FileText className="h-5 w-5 text-brand-600" />
              <div>
                <h2 className="text-lg font-semibold">
                  {specificationService.formatSectionNumber(specification.section_number)}
                </h2>
                <p className="text-sm text-gray-500">{specification.title}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Status and Actions */}
            <div className="flex items-center justify-between mb-6">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${specificationService.getStatusColor(
                  specification.status
                )}`}
              >
                {specification.status.replace(/_/g, ' ')}
              </span>
              <div className="flex items-center space-x-2">
                <a
                  href={specification.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </a>
                <button
                  onClick={onRequestSubstitution}
                  className="flex items-center px-3 py-1.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Request Substitution
                </button>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Division</p>
                <p className="font-medium">
                  {specification.division} -{' '}
                  {specificationService.getDivisionName(specification.division)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Revision</p>
                <p className="font-medium">{specification.revision || 'Initial'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                  Current Version
                </p>
                <p className="font-medium">v{specification.current_version}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Prepared By</p>
                <p className="font-medium">{specification.prepared_by || 'Not specified'}</p>
              </div>
            </div>

            {/* Description */}
            {specification.description && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
                <p className="text-gray-600">{specification.description}</p>
              </div>
            )}

            {/* Submittal Requirement */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Submittal Requirement</h3>
              {specification.submittal_requirement.required ? (
                <div
                  className={`p-4 rounded-lg ${
                    specification.submittal_requirement.submitted ? 'bg-green-50' : 'bg-yellow-50'
                  }`}
                >
                  {specification.submittal_requirement.submitted ? (
                    <div className="flex items-center text-green-700">
                      <Check className="h-5 w-5 mr-2" />
                      <span>
                        Submittal received on {specification.submittal_requirement.submitted_at}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center text-yellow-700">
                      <AlertCircle className="h-5 w-5 mr-2" />
                      <span>Submittal required but not yet received</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No submittal required</p>
              )}
            </div>

            {/* Version History */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Version History</h3>
              <div className="space-y-2">
                {specification.versions.map(version => (
                  <div
                    key={version.version}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium">v{version.version}</span>
                      <span className="text-sm text-gray-500">
                        {new Date(version.uploaded_at).toLocaleDateString()}
                      </span>
                      {version.description && (
                        <span className="text-sm text-gray-500">- {version.description}</span>
                      )}
                    </div>
                    <a
                      href={version.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 hover:bg-gray-200 rounded"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================
// Request Substitution Drawer
// ============================
interface RequestSubstitutionDrawerProps {
  projectId: string;
  specification: ProjectSpecification;
  onClose: () => void;
  onSubmitted: () => void;
}

const RequestSubstitutionDrawer: React.FC<RequestSubstitutionDrawerProps> = ({
  projectId,
  specification,
  onClose,
  onSubmitted,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateSubstitutionData>({
    spec_id: specification._id,
    original_product: '',
    proposed_product: '',
    manufacturer: '',
    model_number: '',
    reason: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) return;

    setSubmitting(true);
    try {
      const result = await specificationService.createSubstitution(projectId, formData);
      if (result.success) {
        onSubmitted();
      }
    } catch (error) {
      console.error('Failed to submit substitution:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFieldChange = (field: keyof CreateSubstitutionData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={() => !submitting && onClose()}
      />
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[600px] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-brand-600" />
              <h2 className="text-lg font-semibold">Request Product Substitution</h2>
            </div>
            <button
              onClick={() => !submitting && onClose()}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Specification Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                For Specification
              </p>
              <p className="font-medium">
                {specificationService.formatSectionNumber(specification.section_number)} -{' '}
                {specification.title}
              </p>
            </div>

            {/* Original Product */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Original Product <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.original_product}
                onChange={e => handleFieldChange('original_product', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="Product specified in document"
                required
              />
            </div>

            {/* Proposed Product */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proposed Product <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.proposed_product}
                onChange={e => handleFieldChange('proposed_product', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="Proposed substitute product"
                required
              />
            </div>

            {/* Manufacturer & Model */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manufacturer <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.manufacturer}
                  onChange={e => handleFieldChange('manufacturer', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Manufacturer name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model Number</label>
                <input
                  type="text"
                  value={formData.model_number}
                  onChange={e => handleFieldChange('model_number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Model/part number"
                />
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Substitution <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.reason}
                onChange={e => handleFieldChange('reason', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                rows={4}
                placeholder="Explain why you are requesting this substitution"
                required
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
              <p className="font-medium mb-1">Note:</p>
              <p>
                After submitting, you can attach supporting documents such as cut sheets, comparison
                charts, and certifications to strengthen your request.
              </p>
            </div>
          </form>

          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={
                  submitting ||
                  !formData.original_product ||
                  !formData.proposed_product ||
                  !formData.manufacturer ||
                  !formData.reason
                }
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================
// Review Substitution Drawer
// ============================
interface ReviewSubstitutionDrawerProps {
  projectId: string;
  substitution: ProductSubstitution;
  onClose: () => void;
  onReviewed: () => void;
}

const ReviewSubstitutionDrawer: React.FC<ReviewSubstitutionDrawerProps> = ({
  projectId,
  substitution,
  onClose,
  onReviewed,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState<'approved' | 'rejected' | 'approved_as_noted' | ''>('');
  const [reviewNotes, setReviewNotes] = useState('');

  const handleSubmitReview = async () => {
    if (!projectId || !decision) return;

    setSubmitting(true);
    try {
      const reviewData: ReviewSubstitutionData = {
        status: decision,
        review_notes: reviewNotes || undefined,
      };

      const result = await specificationService.reviewSubstitution(
        projectId,
        substitution._id,
        reviewData
      );
      if (result.success) {
        onReviewed();
      }
    } catch (error) {
      console.error('Failed to submit review:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const isReviewable =
    substitution.status === 'submitted' || substitution.status === 'under_review';

  return (
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[700px] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-brand-600" />
              <div>
                <h2 className="text-lg font-semibold">Substitution Request</h2>
                <p className="text-sm text-gray-500">{substitution.substitution_id}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Status */}
            <div className="flex items-center justify-between mb-6">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${specificationService.getSubstitutionStatusColor(
                  substitution.status
                )}`}
              >
                {substitution.status.replace(/_/g, ' ')}
              </span>
              <span className="text-sm text-gray-500">
                Submitted: {new Date(substitution.submitted_at).toLocaleDateString()}
              </span>
            </div>

            {/* Specification Reference */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Specification</p>
              <p className="font-medium">
                {substitution.spec_section} - {substitution.spec_title}
              </p>
            </div>

            {/* Product Comparison */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                  Original Product
                </p>
                <p className="font-medium">{substitution.original_product}</p>
              </div>
              <div className="border border-brand-200 bg-brand-50 rounded-lg p-4">
                <p className="text-xs text-brand-600 uppercase tracking-wide mb-2">
                  Proposed Product
                </p>
                <p className="font-medium">{substitution.proposed_product}</p>
                <p className="text-sm text-gray-600 mt-1">{substitution.manufacturer}</p>
                {substitution.model_number && (
                  <p className="text-sm text-gray-500">Model: {substitution.model_number}</p>
                )}
              </div>
            </div>

            {/* Reason */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Reason for Substitution</h3>
              <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{substitution.reason}</p>
            </div>

            {/* Requested By */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Requested By</h3>
              <p className="text-gray-600">
                {substitution.requested_by_name}
                {substitution.requested_by_company && ` (${substitution.requested_by_company})`}
              </p>
            </div>

            {/* Attachments */}
            {substitution.attachments.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Attachments</h3>
                <div className="space-y-2">
                  {substitution.attachments.map(att => (
                    <div
                      key={att._id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-500" />
                        <div>
                          <p className="font-medium text-sm">{att.name}</p>
                          <p className="text-xs text-gray-500">
                            {att.file_type.replace(/_/g, ' ')}
                          </p>
                        </div>
                      </div>
                      <a
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 hover:bg-gray-200 rounded"
                      >
                        <Download className="h-4 w-4" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Previous Review */}
            {substitution.reviewed_by && (
              <div className="mb-6 border-t border-gray-200 pt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Review Decision</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-medium mb-1">{substitution.reviewed_by_name}</p>
                  <p className="text-sm text-gray-500 mb-2">
                    {new Date(substitution.reviewed_at || '').toLocaleDateString()}
                  </p>
                  {substitution.review_notes && (
                    <p className="text-gray-600">{substitution.review_notes}</p>
                  )}
                </div>
              </div>
            )}

            {/* Review Form */}
            {isReviewable && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-4">Make Decision</h3>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => setDecision('approved')}
                    className={`p-4 rounded-lg border-2 text-center transition-colors ${
                      decision === 'approved'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <Check className="h-6 w-6 mx-auto mb-2" />
                    <span className="font-medium">Approve</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDecision('approved_as_noted')}
                    className={`p-4 rounded-lg border-2 text-center transition-colors ${
                      decision === 'approved_as_noted'
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                        : 'border-gray-200 hover:border-cyan-300'
                    }`}
                  >
                    <Check className="h-6 w-6 mx-auto mb-2" />
                    <span className="font-medium">Approve as Noted</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDecision('rejected')}
                    className={`p-4 rounded-lg border-2 text-center transition-colors ${
                      decision === 'rejected'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 hover:border-red-300'
                    }`}
                  >
                    <X className="h-6 w-6 mx-auto mb-2" />
                    <span className="font-medium">Reject</span>
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review Notes{' '}
                    {decision === 'approved_as_noted' && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    rows={3}
                    placeholder="Add review comments or notes"
                  />
                </div>
              </div>
            )}
          </div>

          {isReviewable && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReview}
                  disabled={
                    submitting || !decision || (decision === 'approved_as_noted' && !reviewNotes)
                  }
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Specifications;
